<template>
  <div class="psReportsAltasBajas">
    <div class="container">
      <div class="reportHeader">
        <el-button type="primary" size="mini" round :disabled="selectedRows.length == 0" @click="openBulkEventDialog">Crear evento</el-button>
        <span>{{count}} pacientes</span>
      </div>

      <ps-criteria-filters
        ref="filters"
        :config="filtersConfig"
        :has-results="rows.length > 0"
        :printing="printing"
        @filter="loadReport"
        @print="printReport">
      </ps-criteria-filters>

      <el-table
        v-loading="loading"
        :data="rows"
        size="mini"
        stripe
        height="calc(100vh - 330px)"
        empty-text="No hay movimientos para los filtros seleccionados"
        @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45"></el-table-column>
        <el-table-column prop="patientId" label="Código" width="75"></el-table-column>
        <el-table-column label="Paciente" min-width="190" show-overflow-tooltip>
          <template slot-scope="scope">
            <strong><a :href="urlPatientDetail(scope.row.patientId)" target="_blank" style="color: #2c3e50;">{{patientName(scope.row)}}</a></strong>
            <div v-if="scope.row.nickname" class="secondaryData">{{scope.row.nickname}}</div>
          </template>
        </el-table-column>
        <el-table-column label="Estado actual" width="115">
          <template slot-scope="scope">
            <el-tag :type="scope.row.currentActive == 1 ? 'success' : 'info'" size="mini">
              {{scope.row.currentStatus}}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Movimiento" width="105">
          <template slot-scope="scope">{{capitalize(scope.row.movementType)}}</template>
        </el-table-column>
        <el-table-column label="Fecha movimiento" width="135">
          <template slot-scope="scope">{{formatDate(scope.row.movementDate)}}</template>
        </el-table-column>
        <el-table-column label="Fecha inicio" width="110">
          <template slot-scope="scope">{{formatDate(scope.row.startDate)}}</template>
        </el-table-column>
        <el-table-column prop="groupDescription" label="Grupo" min-width="125" show-overflow-tooltip></el-table-column>
        <el-table-column prop="phaseDescription" label="Fase" min-width="125" show-overflow-tooltip></el-table-column>
        <el-table-column label="Peso" width="85" align="right">
          <template slot-scope="scope">{{formatWeight(scope.row.weight)}}</template>
        </el-table-column>
        <el-table-column fixed="right" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" icon="el-icon-date" @click="openEventDialog(scope.row)">Evento</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <ps-event-dialog
      :visible.sync="eventDialogVisible"
      :patient="targetPatients.length > 1 ? 'múltiples pacientes' : selectedPatient"
      :default-event="'4'"
      @save="save"
    />
  </div>
</template>

<script>
import PsCriteriaFilters from '@/shared/components/PsCriteriaFilters.vue'
import PsEventDialog from '@/shared/components/PsEventDialog.vue'
import reportsFiltersConfig from '@/views/reports/models/reports_filters_config'
import { buildCriteria, initialCriteriaValues, serializeCriteria } from '@/shared/criteria/criteria_utils'
import { getAltasBajas, getAltasBajasPdf } from '@/services/reports_service'
import calendarEventsMixin from '@/shared/mixins/calendarEventsMixin'

export default {
  name: 'PsReportsAltasBajas',
  components: { PsCriteriaFilters, PsEventDialog },
  mixins: [calendarEventsMixin],
  data () {
    return {
      filtersConfig: reportsFiltersConfig.altasBajas,
      rows: [],
      count: 0,
      loading: false,
      printing: false,
      queryString: '',
      selectedRows: [],
      targetPatients: [],
      eventDialogVisible: false,
      savingEvent: false
    }
  },
  computed: {
    selectedPatient () {
      const patient = this.targetPatients[0]
      if(!patient) return ''
      return patient
    },
  },
  methods: {
    async save(events) {
      for(const event of events) {
        await this.saveEvents(event)
      }
      this.eventDialogVisible = false;
    },
    async loadReport (queryString) {
      this.loading = true
      try {
        const result = await getAltasBajas(queryString)
        this.rows = result.data
        this.count = result.count
        this.queryString = queryString
      } catch (err) {
        this.rows = []
        this.count = 0
        const message = err.response && err.response.data
          ? err.response.data.error || err.response.data.err && err.response.data.err.details
          : 'No se pudo cargar el reporte'
        this.$message({ message, type: 'error' })
      } finally {
        this.loading = false
      }
    },
    async printReport () {
      const pdfWindow = window.open('', '_blank')
      if (!pdfWindow) {
        this.$message({ message: 'El navegador bloqueó la pestaña del PDF', type: 'warning' })
        return
      }
      this.printing = true
      try {
        const result = await getAltasBajasPdf(this.queryString)
        const pdfUrl = URL.createObjectURL(new Blob([result.data], { type: 'application/pdf' }))
        pdfWindow.location.href = pdfUrl
        window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000)
      } catch (err) {
        pdfWindow.close()
        this.$message({ message: 'No se pudo generar el PDF', type: 'error' })
      } finally {
        this.printing = false
      }
    },         
    urlPatientDetail (patientId) {
      return `${process.env.VUE_APP_URLAPI}/patients/detail/${patientId}`
    },
    onSelectionChange (rows) {
      this.selectedRows = rows
    },
    openEventDialog (row) {
      this.targetPatients = [row]
      this.eventDialogVisible = true
    },
    openBulkEventDialog () {
      const unique = new Map()
      this.selectedRows.forEach(row => {
        if (!unique.has(row.patientId)) unique.set(row.patientId, row)
      })
      this.targetPatients = [...unique.values()]
      this.eventDialogVisible = true
    },
    patientName (row) {
      return `${row.name || ''} ${row.surname || ''}`.trim()
    },
    capitalize (value) {
      if (!value) return ''
      return value.substring(0, 1).toUpperCase() + value.substring(1)
    },
    formatDate (value) {
      if (!value) return ''
      const date = String(value).substring(0, 10).split('-')
      return date.length == 3 ? `${date[2]}/${date[1]}/${date[0]}` : value
    },
    formatWeight (value) {
      return value === null || value === undefined || Number(value) == 0
        ? '-'
        : `${Number(value).toFixed(1)} kg`
    }
  },
  created () {
    const values = initialCriteriaValues(this.filtersConfig)
    this.loadReport(serializeCriteria(buildCriteria(this.filtersConfig, values)))
  }
}
</script>

<style scoped>
.psReportsAltasBajas { height: calc(100vh - 130px); padding: 0; }
.container {
  background-color: white;
  border-radius: 20px;
  padding: 25px 32px 32px;
  height: 100%;
  box-sizing: border-box;
  margin-top: 10px;
}
.reportHeader { display: flex; justify-content: flex-end; align-items: center; margin-bottom: 10px; }
.reportHeader .el-button { margin-right: 15px; }
.reportHeader span { color: #777; font-size: 0.85rem; }
.el-table { margin-top: 20px; width: 100%; }
.secondaryData { color: #888; font-size: 0.75rem; }

@media (max-width: 991.98px) {
  .psReportsAltasBajas { height: calc(100vh - 150px); padding: 0 10px 20px; }
  .container { padding: 20px 15px; overflow: hidden; }
}
</style>
